/**
 * Feedback Bridge — connects @cogo/validation-rules feedback to cogo-validator.
 *
 * Provides:
 * 1. bundleToBlockData() — converts BlockBundle to BlockData for rule checking
 * 2. generatePromptFeedback() — runs validation-rules and formats for LLM prompt injection
 * 3. generatePromptFeedbackFromApp() — runs cross-block validation and formats for LLM
 */

import type { BlockBundle } from "./types"
import type { BlockData, PromptFeedback } from "@cogo/validation-rules"
import {
  generateFeedback,
  formatFeedbackForPrompt,
  getFeaturePatternRules,
} from "@cogo/validation-rules"
import { runCrossBlockStage } from "./stages/stage-cross-block"

/**
 * Convert a cogo-validator BlockBundle into a validation-rules BlockData.
 * BlockData is a minimal subset used by shared rules.
 */
export function bundleToBlockData(bundle: BlockBundle): BlockData {
  return {
    block: (bundle.block ?? {}) as Record<string, unknown>,
    ui: (bundle.ui ?? {}) as Record<string, unknown>,
    events: (bundle.events ?? {}) as Record<string, unknown>,
    flow: (bundle.flow ?? {}) as Record<string, unknown>,
    variables: (bundle.variables ?? {}) as Record<string, unknown>,
    datasource: (bundle.datasource ?? {}) as Record<string, unknown>,
  }
}

/**
 * Run all shared validation-rules on a BlockBundle and return formatted prompt feedback.
 *
 * Usage in generation retry loop:
 *   const feedback = generatePromptFeedbackFromBundle(bundle)
 *   if (feedback) retryPrompt += "\n" + feedback
 */
export function generatePromptFeedbackFromBundle(bundle: BlockBundle): string {
  const blockData = bundleToBlockData(bundle)
  const feedback = generateFeedback(blockData)
  return formatFeedbackForPrompt(feedback)
}

/**
 * Run cross-block validation and individual block validations on an app set,
 * formatting all errors and composition mismatches into a single cohesive
 * feedback prompt for LLM self-healing.
 */
export function generatePromptFeedbackFromApp(
  blocks: Record<string, BlockBundle>,
  appConfig?: Record<string, unknown>
): string {
  // 1. Run cross-block consistency validation
  const crossBlockReport = runCrossBlockStage(blocks, appConfig)
  const crossBlockIssues = crossBlockReport.issues

  let crossBlockFeedback = ""
  if (crossBlockIssues.length > 0) {
    const errors = crossBlockIssues.filter((i) => i.level === "error")
    const warnings = crossBlockIssues.filter((i) => i.level === "warning")

    if (errors.length > 0 || warnings.length > 0) {
      crossBlockFeedback += "### Block Composition & State Consistency Violations\n"
      crossBlockFeedback += "The generated multi-block application violates semantic composition rules and data consistency guidelines. Please fix the following issues in the next generation iteration:\n\n"

      for (const issue of [...errors, ...warnings]) {
        const severity = issue.level.toUpperCase()
        const blockCtx = issue.block ? ` in block [${issue.block}]` : ""
        crossBlockFeedback += `- **${severity}** (${issue.rule})${blockCtx}: ${issue.message}\n`
      }
      crossBlockFeedback += "\n"
    }
  }

  // 2. Gather individual block feedbacks
  let blockFeedbacks = ""
  for (const [blockName, bundle] of Object.entries(blocks)) {
    const feedback = generatePromptFeedbackFromBundle(bundle)
    if (feedback.trim()) {
      blockFeedbacks += `### Individual Block Feedback: [${blockName}]\n`
      blockFeedbacks += feedback + "\n\n"
    }
  }

  return (crossBlockFeedback + blockFeedbacks).trim()
}

/**
 * Get the raw PromptFeedback object (for structured access to errors/warnings/additions).
 */
export function getPromptFeedback(bundle: BlockBundle): PromptFeedback {
  const blockData = bundleToBlockData(bundle)
  return generateFeedback(blockData)
}

/** Re-export getFeaturePatternRules for prompt hardening base rules */
export { getFeaturePatternRules }

